import type { PagedGrid } from 'BrokerTsLib/src/dtos/Shared/PagedGrid'
import type { PagedQuery } from 'BrokerTsLib/src/dtos/Shared/PagedQuery'

// Numeric enums — the backend serializes/binds enums as numbers (no string-enum converter).
export const SagaEventTypes = {
  CANCELLATION: 0,
  STOCK_ACTIVATION: 1,
  POLICY_UPDATE: 2,
  REPORTING_SFTP: 3,
  REPORTING_EXPORT: 4
} as const
export type SagaEventTypes = typeof SagaEventTypes[keyof typeof SagaEventTypes]

export const ErrorSource = {
  INTERNAL: 0,
  INSURER: 1,
} as const
export type ErrorSource = typeof ErrorSource[keyof typeof ErrorSource]

// Komunikační logy "Typ" column — backend HttpRequestLogType, serialized as number.
export const HttpRequestLogType = {
  CancelContract: 0,
  GetContractInfo: 1,
  CreateStock: 2,
  CalculateStock: 3
} as const
export type HttpRequestLogType = typeof HttpRequestLogType[keyof typeof HttpRequestLogType]

export const httpRequestLogTypeLabels: Record<HttpRequestLogType, string> = {
  [HttpRequestLogType.CancelContract]: 'Ukončení smlouvy',
  [HttpRequestLogType.GetContractInfo]: 'Informace o smlouvě',
  [HttpRequestLogType.CreateStock]: 'Tvorba skladového pojištění',
  [HttpRequestLogType.CalculateStock]: 'Kalkulace skladového pojištění'
}

// §6.2 — predefined filters, all ON_HOLD by default.
export const ErrorHandlingSagaPreset = {
  Unassigned: 0,   // "Nepřiřazené chyby" (default)
  Mine: 1,         // "Moje chyby"
  All: 2,          // "Všechny chyby" — every error regardless of assignment
} as const
export type ErrorHandlingSagaPreset = typeof ErrorHandlingSagaPreset[keyof typeof ErrorHandlingSagaPreset]

// "Typ požadavku" column.
export const sagaEventLabels: Record<SagaEventTypes, string> = {
  [SagaEventTypes.CANCELLATION]: 'Ukončení pojištění',
  [SagaEventTypes.STOCK_ACTIVATION]: 'Tvorba skladového pojištění',
  [SagaEventTypes.POLICY_UPDATE]: 'Informativní změna',
  [SagaEventTypes.REPORTING_SFTP]: 'Off-line reporting (SFTP)',
  [SagaEventTypes.REPORTING_EXPORT]: 'Off-line reporting (Export)',
}

export const errorSourceLabels: Record<ErrorSource, string> = {
  [ErrorSource.INTERNAL]: 'Interní',
  [ErrorSource.INSURER]: 'Pojišťovna',
}

// QUEUED: a per-key FIFO-serialized retryable (e.g. POLICY_UPDATE) waiting for an earlier same-key saga to finish.
// CANCELED: operator stopped a non-terminal saga (terminal; frees the BusinessKey so a resend runs fresh).
export type RetryableSagaState = 'PENDING_START' | 'PENDING_RETRY' | 'QUEUED' | 'ON_HOLD' | 'RESOLVED' | 'DISCARDED' | 'CANCELED'
export type ManualSagaState = 'ON_HOLD' | 'RESOLVED' | 'DISCARDED'
export type ErrorHandlingSagaState = RetryableSagaState | ManualSagaState

export interface ErrorHandlingSagaGridItem {
  id: string
  createdAt: string
  eventType?: SagaEventTypes
  leasingContractNumber?: string
  contractNumber?: string
  insurerId?: number
  errorSource?: ErrorSource
  attemptCount: number
  lastAttemptAt?: string
  processingType: string
  state: ErrorHandlingSagaState
  assigneeId?: string
  assigneeName?: string
  resolvedById?: string
  resolvedByName?: string
  note?: string
}

export type ErrorHandlingSagaPagedGrid = PagedGrid<ErrorHandlingSagaGridItem>

export interface ErrorHandlingSagaPagedQuery extends PagedQuery {
  preset: ErrorHandlingSagaPreset
  eventType?: SagaEventTypes
  state?: ErrorHandlingSagaState
  processingType?: string
  insurerId?: number
  errorSource?: ErrorSource
  contractNumber?: string
  leasingContractNumber?: string
  createdFrom?: string
  createdTo?: string
}

export interface SagaAuditLogDto {
  id: number
  messageType: string
  success: boolean
  error?: string
  errorSource?: ErrorSource
  reason?: string
  // Saga state before/after this delivery (NewState == PreviousState on a failed, rolled-back delivery).
  previousState?: string
  newState?: string
  request?: string
  startedAt: string
  completedAt: string
  // HTTP/SOAP comms logs produced by this attempt (nested server-side).
  logs: HttpRequestLogHeader[]
}

export interface HttpRequestLogHeader {
  id: number
  requestId: string
  type: HttpRequestLogType
  requestUri: string
  requestStartTime: string
  requestEndTime: string
  statusCode?: number
  dataId: string
  insuranceParamsId?: number
  insurerOfferId?: number
  contractNumber?: string
}

export interface HttpHeaderKvp {
  key: string
  value: string[]
}

// Stored request/response payload of a single log row (detail "Logy" drill-down).
export interface HttpRequestLogData {
  requestUri?: string
  defaultRequestHeaders?: HttpHeaderKvp[]
  contentHeaders?: HttpHeaderKvp[]
  responseHeaders?: HttpHeaderKvp[]
  statusCode?: number
  requestContent?: string
  responseContent?: string
  error?: { message?: string; stackTrace?: string }
}

export interface ErrorHandlingSagaDetail {
  id: string
  requestId: string
  createdAt: string
  createdById: string
  createdByName?: string
  eventType?: SagaEventTypes
  processingType: string
  state: string
  contractNumber?: string
  leasingContractNumber?: string
  insurerId?: number
  insuranceId?: number
  insuranceParamsId?: number
  insurerOfferId?: number
  attemptCount: number
  lastAttemptAt?: string
  assigneeId?: string
  assigneeName?: string
  resolvedById?: string
  resolvedByName?: string
  note?: string
  attempts: SagaAuditLogDto[]
  // Logs matching no attempt envelope: the pre-saga first attempt + pre-change historical logs.
  unmatchedLogs: HttpRequestLogHeader[]
}
